import React, { useState } from "react";
import shortid from "shortid";
import ModuleHeader from "../modules/ModuleHeader";
import { useKeyTerms } from "../../hooks/useKeyTerms";
import { useClearCurrentTopic } from "../../hooks/useClearCurrentTopic";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const waterfall = [
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT STARTING FROM THE BEGINNING",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOUR SPONSOR:",
        questions: [
          "Can you help me understand where this project fits in to our organization’s strategy?",
          "What are the top three business outcomes we need to deliver, and by when?",
          "What are the top three metrics that should be captured and reported on?",
          "Are there any other stakeholders that I should involve when planning that were not accounted for?",
          "Does my department have any templates that would make my life easier in the planning phase and help with alignment?",
        ],
      },
    ],
  },
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT GETTING IT UP AND RUNNING",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOUR SPONSOR:",
        questions: [
          "Are you my one-and-only sponsor?",
          "Are there any potential risks that you can already see?",
          "Does your sponsor agree with the list of requirements you have gathered so far?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOURSELF:",
        questions: [
          "Do you have a process for continually tracking and documenting requirements? Is it working for you?",
          "Can you list all your stakeholders?",
          "Do you know their motivations and needs?",
        ],
      },
    ],
  },
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT PLANNING YOUR PROJECT WORK",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOURSELF:",
        questions: [
          "Have I identified all the team members who need to weigh in on this?",
          "Were they actively engaged in this process?",
          "Does each team member have the skills and experience necessary to complete their tasks?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK THE TEAM:",
        questions: [
          "Do all team members feel comfortable with the tasks that were recorded in this process?",
          "Have all the tasks been determined?",
          "Does the team agree with the network diagram you created? Can they identify any gaps?",
        ],
      },
    ],
  },
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT GETTING THE WORK DONE",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOUR SPONSOR:",
        questions: [
          "Can your Sponsor think of any risks that might occur based on historical information or previous similar projects?",
          "You might want to run your presentation by your Sponsor, just to make sure everyone is aligned.",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK THE TEAM:",
        questions: [
          "Can everyone access the schedule?",
          "Does everyone feel their tasks are represented accurately?",
          "Are there any other dependencies that have been overlooked?",
          "Can anyone spot any risks or opportunities coming down the pike?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOURSELF:",
        questions: [
          "Does your presentation get YOU excited to work on this? Will it motivate others?",
        ],
      },
    ],
  },
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT SHAPING THE PROJECT AS IT GOES",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOUR SPONSOR:",
        questions: [
          "Am I tracking all the right things? Do your metrics need to be updated?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOURSELF:",
        questions: [
          "Has everyone who matters had a chance to review your plan?",
          "Are your metrics matching up with your milestones? If not, do you need to course-correct?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK ALL STAKEHOLDERS:",
        questions: [
          "Does the method that you’ve outlined work for each group of stakeholders?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK THE TEAM:",
        questions: [
          "Are we reporting out the right things at the right time to the right people?",
          "What could we have done better?",
          "What did we do really well?",
        ],
      },
    ],
  },
];

const agile = [
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT STARTING FROM THE BEGINNING",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOURSELF:",
        questions: [
          "Does my idea, or the thing I’m being asked to do, align with, or lend itself, to an agile mindset?",
          "Who is my sponsor?",
          "Who is my customer?",
        ],
      },
    ],
  },
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT GETTING IT UP AND RUNNING",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOUR SPONSOR:",
        questions: [
          "Does your sponsor have a sense of the project objectives?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOURSELF:",
        questions: [
          "Have I uncovered as many risks as possible? Are they all addressed?",
          "Is your Scrum Master or Disciplined Agile Lean Scrum Master experienced?",
          "Do your team members have the behaviors and characteristics to succeed in an agile environment?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK THE TEAM:",
        questions: [
          "Have we listed all the project assumptions we can think of?",
          "Do your sponsor and team members understand the nature of an agile way of working and support the choice to be agile?",
          "Does everyone have what they need to be comfortable and productive?",
          "Does everyone understand and agree with the working agreements?",
        ],
      },
    ],
  },
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT GETTING THE WORK DONE",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOURSELF:",
        questions: [
          "Do I have an idea of the value of these features based on my customer and team feedback?",
          "Does the work being demonstrated meet your expectations?",
          "Can you articulate to the team those things that need to be changed?",
          "Has your team delivered the work they committed to deliver? If not, why not?",
          "Did the Scrum Master or Disciplined Agile Lean Scrum Master do a good job of setting a tone of safety and openness for the meeting?",
          "Did team members appear to feel safe enough to talk about their pain points, identify ways that they can improve their performance, and ask for help?",
          "Were there clear lessons learned?",
          "Did these lessons lead to actionable adjustments the team can make?",
          "Did the team actually execute on the actionable adjustments in the upcoming sprint?",
          "Are your metrics easily accessible by the project Sponsor and other stakeholders that require regular updates?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK THE TEAM:",
        questions: [
          "What is the ideal duration of the sprint for this project?",
          "Do the user stories address who needs the product, what they need, and why they need it?",
          "Do my user stories logically roll up into epics and again into product features?",
          "Do my user stories include acceptance criteria to validate that we delivered the required value?",
          "Has the Scrum Master or Disciplined Agile Lean Scrum Master worked with the team to determine the tasks that will be completed during the sprint?",
          "Are the user story point estimates in the sprint agreed to by the team members?",
          "Does everyone understand the work they need to complete during this upcoming sprint?",
          "Does the workload realistically match the team’s capacity?",
          "Has my Scrum Master or Disciplined Agile Lean Scrum Master scheduled this daily meeting with the team?",
          "Are all team members aware of the importance of this meeting?",
          "Do all team members show up to the meeting each day, prepared to share information about their work and any barriers they may be experiencing?",
          "Is the team communicating well with each other?",
          "Is the environment supportive?",
          "Do we all understand that the Scrum approach may result in initial iterations of the product that are not perfect?",
          "Does the team understand that ambiguity will lessen as the project progresses?",
          "Do the team and stakeholders understand that responding to new information and changes makes for a better product in the end?",
          "Is everyone comfortable with the amount of work they are committing to?",
        ],
      },
    ],
  },
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT RELEASING TO OPERATIONS",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK THE TEAM:",
        questions: [
          "What could we have done better?",
          "What did we do really well?",
          "How and when can we share this information with people who can learn from it?",
        ],
      },
    ],
  },
  {
    sectionId: shortid(),
    sectionTitle: "QUESTIONS ABOUT SHAPING THE PROJECT AS IT GOES",
    questions: [
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOUR SPONSOR:",
        questions: [
          "Am I tracking all the right things? Do your metrics need to be updated?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK YOURSELF:",
        questions: [
          "Has everyone who matters had a chance to review your plan?",
          "Are your metrics matching up with your milestones? If not, do you need to course-correct?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK ALL STAKEHOLDERS:",
        questions: [
          "Does the method that you’ve outlined work for each group of stakeholders?",
        ],
      },
      {
        questionsId: shortid(),
        questionsTitle: "QUESTIONS TO ASK THE TEAM:",
        questions: [
          "Are we reporting out the right things at the right time to the right people?",
          "What could we have done better?",
          "What did we do really well?",
        ],
      },
    ],
  },
];

const GoodQuestions = () => {
  useClearCurrentTopic();
  const [visible, setVisible] = useState([]);
  useKeyTerms();
  const section = useSelector((state) => state.progress.section);
  const { t } = useTranslation();

  const data = section === "PMAgile" ? agile : waterfall;

  const showSection = (id) => {
    return visible.includes(id);
  };

  const toggleCollapse = (id) => {
    if (visible.includes(id)) {
      setVisible(visible.filter((i) => i !== id));
    } else {
      setVisible([...visible, id]);
    }
  };

  return (
    <div>
      <ModuleHeader
        bgColor="white"
        color="darkgray"
        hoverColor="black"
        title=""
        showBackButton={false}
        backUrl={`/app`}
      />

      <div className="content-area flex-grow">
        <div className="inner-content px-15 sm:px-55 lg:px-15 mx-auto pb-25 lg:pb-8">
          <div className="text-gray-200 light-gray-block relative pt-47 pb-12">
            <h1 className="uppercase mt-0 mb-20 text-20 leading-tight text-mineshaft tracking-wide font-secondary font-bold">
              {t("questionsToAsk")}
            </h1>
            <p>{t("questionPageText")} </p>
          </div>

          {data.map((section) => (
            <div key={section.sectionId} className="pt-24 lg:pl-35">
              <h2 className="uppercase font-primary font-bold text-base relative mb-24 pl-30 sm:pl-0">
                <span
                  onClick={() => toggleCollapse(section.sectionId)}
                  className={`icon-triangle-down absolute top-17 ssm:top-5 left-0 sm:-left-35 text-20 cursor-pointer`}
                  style={{
                    transform: showSection(section.sectionId)
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                  }}
                />
                {section.sectionTitle}
              </h2>
              {showSection(section.sectionId) &&
                section.questions.map((questionsSection) => (
                  <div
                    key={questionsSection.questionsId}
                    className="pl-30 pb-30"
                  >
                    <h3 className="uppercase font-primary font-normal text-base relative mb-16">
                      <span
                        onClick={() =>
                          toggleCollapse(questionsSection.questionsId)
                        }
                        className={`icon-triangle-down absolute top-17 ssm:top-5 left-0 sm:-left-35 text-20 cursor-pointer`}
                        style={{
                          transform: showSection(questionsSection.questionsId)
                            ? "rotate(0deg)"
                            : "rotate(-90deg)",
                        }}
                      />
                      {questionsSection.questionsTitle}
                    </h3>

                    <ol className="mb-36">
                      {showSection(questionsSection.questionsId) &&
                        questionsSection.questions.map((item, index) => (
                          <li key={`q-${index}`} className="mb-20">
                            {item}
                          </li>
                        ))}
                    </ol>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GoodQuestions;
